import React from 'react';

interface PostPreviewProps {
  formatId: string;
  rendererKind: 'text' | 'carousel' | 'thread' | 'poll' | null;
  /** Raw rendition content. For text/single-tweet formats this is markdown/text;
   *  for thread/carousel/poll formats it's the JSON payload from the renderer. */
  content: string;
}

export interface PollPayload {
  caption: string;
  question: string;
  options: string[];
  durationDays: number;
  comment: string;
  hashtags: string[];
}

export interface ThreadPayload {
  tweets: { n: number; text: string }[];
}

export interface CarouselPayload {
  caption: string;
  hashtags: string[];
  /** Either base64-encoded PNG (no data: prefix) when sharp succeeded, or
   *  raw SVG strings when satori-without-sharp ran, or raw SVG fallback
   *  strings when satori itself failed. */
  slides: string[];
}

export function tryParseThread(s: string): ThreadPayload | null {
  try {
    const j = JSON.parse(s);
    if (j && Array.isArray(j.tweets)) return j as ThreadPayload;
  } catch { /* swallow */ }
  return null;
}

export function tryParseCarousel(s: string): CarouselPayload | null {
  try {
    const j = JSON.parse(s);
    if (j && Array.isArray(j.slides) && typeof j.caption === "string") return j as CarouselPayload;
  } catch { /* swallow */ }
  return null;
}

export function tryParsePoll(s: string): PollPayload | null {
  try {
    const j = JSON.parse(s);
    if (j && typeof j.question === "string" && Array.isArray(j.options) && typeof j.caption === "string") {
      return j as PollPayload;
    }
  } catch { /* swallow */ }
  return null;
}

/** LinkedIn's fixed poll durations. */
export function pollDurationLabel(days: number): string {
  return days === 1 ? "1 day" : days === 3 ? "3 days" : days === 7 ? "1 week" : days === 14 ? "2 weeks" : `${days} days`;
}

/** Detect SVG vs base64-PNG slide payload. Base64 PNGs start with `iVBOR…`
 *  (the standard PNG magic header `\x89PNG` encoded). SVG strings start with
 *  `<svg` or `<?xml`. */
export function isSvgSlide(s: string): boolean {
  return /^\s*(<\?xml|<svg)/i.test(s);
}

/**
 * Renders the rendition content per format kind. Threads render tweet-by-tweet
 * with character counts. Text formats render as-is. Carousel formats render
 * a slide-by-slide preview from the JSON when available.
 */
export default function PostPreview({ formatId, rendererKind, content }: PostPreviewProps) {
  if (!content) {
    return (
      <div style={{ padding: 16, border: '1px dashed var(--border)', borderRadius: 0, color: 'var(--text-tertiary)' }}>
        No post generated yet. Click Create.
      </div>
    );
  }

  if (rendererKind === 'carousel') {
    const parsed = tryParseCarousel(content);
    if (parsed) {
      return (
        <div data-testid="post-preview-carousel" data-format-id={formatId} style={{ display: 'grid', gap: 12 }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 12 }}>
            {parsed.slides.map((slide, i) => {
              // SVG path is loaded via an <img> data URI so any <script> or
              // onload handlers an LLM might smuggle into a slide can't execute.
              // PNG path is base64-decoded by the browser.
              const svg = isSvgSlide(slide);
              const b64 = svg
                ? (typeof btoa === 'function' ? btoa(unescape(encodeURIComponent(slide))) : '')
                : slide;
              // #285 QW7: an empty payload would ship as a broken-image box —
              // render a placeholder instead.
              const src = b64 ? `data:image/${svg ? 'svg+xml' : 'png'};base64,${b64}` : '';
              return (
                <figure key={i} style={{ margin: 0, padding: 0, border: '1px solid var(--border)', borderRadius: 0, overflow: 'hidden', background: 'var(--bg-surface)' }}>
                  {src ? (
                    <img
                      src={src}
                      alt={`Slide ${i + 1}`}
                      style={{ display: 'block', width: '100%', height: 'auto', aspectRatio: '1 / 1', objectFit: 'cover' }}
                    />
                  ) : (
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', aspectRatio: '1 / 1', color: 'var(--text-tertiary)', fontSize: 12, textAlign: 'center', padding: 8 }}>
                      Slide {i + 1} preview unavailable
                    </div>
                  )}
                  <figcaption style={{ padding: '6px 8px', fontSize: 11, color: 'var(--text-tertiary)', borderTop: '1px solid var(--border)' }}>Slide {i + 1} / {parsed.slides.length}</figcaption>
                </figure>
              );
            })}
          </div>
          <div style={{ padding: 12, border: '1px solid var(--border)', borderRadius: 0, background: 'var(--bg-surface)' }}>
            <div style={{ fontSize: 11, color: 'var(--text-tertiary)', marginBottom: 6 }}>Caption · {parsed.caption.length} chars</div>
            <div style={{ whiteSpace: 'pre-wrap' }}>{parsed.caption}</div>
            {parsed.hashtags.length > 0 && (
              <div style={{ marginTop: 8, fontSize: 12, color: 'var(--text-secondary)' }}>
                {parsed.hashtags.map((h) => `#${h}`).join(' ')}
              </div>
            )}
          </div>
        </div>
      );
    }
    // fall through to plain-text rendering when JSON envelope is malformed
  }

  if (rendererKind === 'thread') {
    const parsed = tryParseThread(content);
    if (parsed) {
      return (
        <ol style={{ paddingLeft: 0, margin: 0, listStyle: 'none', display: 'grid', gap: 10 }}>
          {parsed.tweets.map((t) => (
            <li key={t.n} style={{ padding: 12, border: '1px solid var(--border)', borderRadius: 0, background: 'var(--bg-surface)' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: 'var(--text-tertiary)', marginBottom: 6 }}>
                <span>Tweet {t.n}/{parsed.tweets.length}</span>
                <span>{t.text.length} chars</span>
              </div>
              <div style={{ whiteSpace: 'pre-wrap' }}>{t.text}</div>
            </li>
          ))}
        </ol>
      );
    }
    // fall through to plain-text rendering
  }

  if (rendererKind === 'poll') {
    const parsed = tryParsePoll(content);
    if (parsed) {
      const cardStyle: React.CSSProperties = { padding: 12, border: '1px solid var(--border)', borderRadius: 0, background: 'var(--bg-surface)' };
      const labelStyle: React.CSSProperties = { fontSize: 11, color: 'var(--text-tertiary)', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.04em' };
      return (
        <div data-testid="post-preview-poll" data-format-id={formatId} style={{ display: 'grid', gap: 12 }}>
          <div style={cardStyle}>
            <div style={labelStyle}>Caption · {parsed.caption.length} chars</div>
            <div style={{ whiteSpace: 'pre-wrap' }}>{parsed.caption}</div>
          </div>
          <div style={cardStyle}>
            <div style={labelStyle}>Poll question</div>
            <div style={{ fontWeight: 600, marginBottom: 10 }}>{parsed.question}</div>
            <ol style={{ margin: 0, paddingLeft: 0, listStyle: 'none', display: 'grid', gap: 6 }}>
              {parsed.options.map((opt, i) => (
                <li key={i} style={{ padding: '8px 12px', border: '1px solid var(--border)', borderRadius: 999, textAlign: 'center' }}>{opt}</li>
              ))}
            </ol>
            <div style={{ marginTop: 10, fontSize: 12, color: 'var(--text-secondary)' }}>Duration: {pollDurationLabel(parsed.durationDays)} · {parsed.options.length} options</div>
          </div>
          <div style={cardStyle}>
            <div style={labelStyle}>Follow-up comment (posted after votes start)</div>
            <div style={{ whiteSpace: 'pre-wrap' }}>{parsed.comment}</div>
            {parsed.hashtags?.length > 0 && (
              <div style={{ marginTop: 8, fontSize: 12, color: 'var(--text-secondary)' }}>{parsed.hashtags.map((h) => `#${h}`).join(' ')}</div>
            )}
          </div>
        </div>
      );
    }
    // fall through to plain-text rendering when the JSON envelope is malformed
  }

  return (
    <pre
      data-testid="post-preview"
      data-format-id={formatId}
      style={{
        margin: 0,
        padding: 12,
        whiteSpace: 'pre-wrap',
        fontFamily: 'inherit',
        border: '1px solid var(--border)',
        borderRadius: 0,
        background: 'var(--bg-surface)',
      }}
    >
      {content}
    </pre>
  );
}
